/**
 * Inline feedback affordance shown under every completed assistant message.
 *
 * Three actions:
 *   - 👍   immediate submit (rating='up'), button stays highlighted afterwards
 *   - 👎   opens FeedbackDownModal (categorised reason + optional comment)
 *   - copy copy the answer text to clipboard with a transient "Copied" tooltip
 *
 * The component is intentionally dumb — it receives the message id + the
 * current feedback state and emits `onSubmit(feedback)` upwards. Persistence
 * (optimistic update + POST) lives in chatStore.submitMessageFeedback.
 */
import { useState } from 'react';
import { Copy, ThumbsDown, ThumbsUp, Check } from 'lucide-react';
import type { ChatMessageFeedback } from '../../stores/chatStore';
import { useT } from '../../i18n';
import { Tooltip } from '../ui/Tooltip';
import { FeedbackDownModal } from './FeedbackDownModal';

interface MessageFeedbackProps {
  messageId: string;
  answerText: string;
  feedback?: ChatMessageFeedback;
  onSubmit: (feedback: ChatMessageFeedback) => void;
}

export function MessageFeedback({ messageId, answerText, feedback, onSubmit }: MessageFeedbackProps) {
  const t = useT();
  const [showDownModal, setShowDownModal] = useState(false);
  const [copied,        setCopied]        = useState(false);

  const isUp   = feedback?.rating === 'up';
  const isDown = feedback?.rating === 'down';

  const handleUp = () => {
    onSubmit({ rating: 'up' });
  };

  const handleDownSubmit = (payload: { category?: string; comment?: string }) => {
    onSubmit({
      rating:   'down',
      category: (payload.category as ChatMessageFeedback['category']) ?? null,
      comment:  payload.comment ?? null,
    });
    setShowDownModal(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(answerText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API rejected (e.g. non-secure context) — silently fail.
    }
  };

  return (
    <>
      <div className="flex items-center gap-1.5 mt-1 -ml-1 text-[var(--text-3)]">
        <Tooltip content={t('chat.feedback.helpful')} width={160}>
          <button
            onClick={handleUp}
            aria-label={t('chat.feedback.helpful')}
            className={`p-1 rounded transition-colors ${
              isUp
                ? 'text-green-600 bg-green-50'
                : 'hover:text-[var(--text-1)] hover:bg-[var(--surface-2)]'
            }`}
          >
            <ThumbsUp size={13} fill={isUp ? 'currentColor' : 'none'} />
          </button>
        </Tooltip>

        <Tooltip content={t('chat.feedback.reportProblem')} width={180}>
          <button
            onClick={() => setShowDownModal(true)}
            aria-label={t('chat.feedback.reportProblem')}
            className={`p-1 rounded transition-colors ${
              isDown
                ? 'text-red-600 bg-red-50'
                : 'hover:text-[var(--text-1)] hover:bg-[var(--surface-2)]'
            }`}
          >
            <ThumbsDown size={13} fill={isDown ? 'currentColor' : 'none'} />
          </button>
        </Tooltip>

        <Tooltip content={copied ? t('chat.feedback.copied') : t('chat.feedback.copy')} width={140}>
          <button
            onClick={handleCopy}
            aria-label={t('chat.feedback.copy')}
            className="p-1 rounded transition-colors hover:text-[var(--text-1)] hover:bg-[var(--surface-2)]"
          >
            {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
          </button>
        </Tooltip>

        {isDown && feedback?.category && (
          <span className="text-[11px] uppercase tracking-wide text-red-600 ml-1">
            {t(`chat.feedback.cat.${feedback.category}`)}
          </span>
        )}
      </div>

      {showDownModal && (
        <FeedbackDownModal
          messageId={messageId}
          existing={feedback}
          onClose={() => setShowDownModal(false)}
          onSubmit={handleDownSubmit}
        />
      )}
    </>
  );
}
